import React, { useState } from 'react';
import PriceTable from '../components/services/PriceTable';
import ServiceFilter from '../components/services/ServiceFilter';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import { useServices } from '../hooks/useServices';
import { categories } from '../data/servicesData';

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center p-8">
    <div className="w-12 h-12 border-4 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
    <p className="mt-4 text-white">Ładowanie usług...</p>
  </div>
);

const ErrorMessage = ({ error }) => (
  <div className="bg-black/30 backdrop-blur-sm rounded-lg p-8 max-w-lg mx-auto text-center">
    <svg 
      className="w-12 h-12 text-red-400 mx-auto mb-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <h3 className="text-xl font-semibold text-white mb-2">
      Nie udało się załadować usług
    </h3>
    <p className="text-gray-300 mb-4">{error}</p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-primary-500/20 hover:bg-primary-500/30 text-white rounded-lg transition-colors duration-200"
    >
      Spróbuj ponownie
    </button>
  </div>
);

const Services = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [titleRef, isTitleVisible] = useIntersectionObserver();
  const [contentRef, isContentVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });
  const { services, loading, error } = useServices();

  const filteredServices = activeCategory
    ? services.filter(service => service.category === activeCategory)
    : services.sort((a, b) => {
        // If both are packages or both are not packages, maintain original order
        if ((a.category === 'Pakiety') === (b.category === 'Pakiety')) {
          return 0;
        }
        // If a is a package, move it to the end
        if (a.category === 'Pakiety') {
          return 1;
        }
        // If b is a package, move it to the end
        return -1;
      });

  return (
    <div className="relative min-h-screen pt-20">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50 z-10" />
        <img
          src="https://images.unsplash.com/photo-1672840997353-4579d5d6a7d5?q=80&w=1932&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover object-center fixed"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-20 container mx-auto px-4 py-12">
        <div
          ref={titleRef}
          className={`text-center mb-12 transform transition-all duration-700 ease-out
            ${isTitleVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Nasze Usługi
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Cennik usług myjni samochodowej w Gdańsku: mycie auta, auto detailing, pranie i czyszczenie tapicerki samochodowej, renowacja skór oraz czyszczenie wnętrza samochodu
          </p>
        </div>

        <div
          ref={contentRef}
          className={`transform transition-all duration-700 ease-out delay-300
            ${isContentVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
        >
          {!error && (
            <div className="flex justify-center mb-12">
              <ServiceFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </div>
          )}

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage error={error} />
          ) : (
            <div className="max-w-3xl mx-auto">
              <PriceTable services={filteredServices} />
              {filteredServices.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-white text-lg">
                    Nie znaleziono usług w tej kategorii
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Services;
